import { useEffect, useRef, useState } from 'react';
import type { ThemePref } from '@hoopify/shared';

export type { ThemePref };

/**
 * Light/dark is saved on each account (coach or admin), so it follows them to any device.
 * This device also remembers the last one used, so the sign-in pages (and the first paint,
 * see public/theme-init.js) match whoever used it last.
 */
const LAST = 'hoopify.theme.last';
/** Where older builds kept a pick per browser. Carried over to the first account that signs in here, then removed. */
const LEGACY = 'hoopify.theme.v2';
/** Even older: saved "system" for everyone, so only an explicit light/dark counts. */
const OLDEST = 'hoopify.theme';

const isPref = (v: unknown): v is ThemePref => v === 'light' || v === 'dark' || v === 'system';

function get(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // storage blocked
  }
}

/** A pick made on this device before themes were saved on accounts, if any. */
export function legacyPick(): ThemePref | null {
  const v = get(LEGACY);
  if (isPref(v)) return v;
  const old = get(OLDEST);
  return old === 'light' || old === 'dark' ? old : null;
}

function clearLegacy() {
  try {
    localStorage.removeItem(LEGACY);
    localStorage.removeItem(OLDEST);
  } catch {
    /* fine */
  }
}

export function applyTheme(pref: ThemePref) {
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  try {
    localStorage.setItem(LAST, pref);
  } catch {
    /* private mode: it just won't be remembered on this device */
  }
}

/**
 * The signed-in person's theme. `saved` is what's on their account (null until they choose:
 * shown dark); `save` stores a new choice on the account. Use once per layout.
 */
export function useAccountTheme(saved: ThemePref | null, save: (p: ThemePref) => Promise<unknown>) {
  const [pending, setPending] = useState<ThemePref | null>(null);
  const pref: ThemePref = pending ?? saved ?? 'dark';
  const saveRef = useRef(save);
  saveRef.current = save;

  // First sign-in since themes moved to accounts: keep what they'd picked on this device.
  useEffect(() => {
    const legacy = legacyPick();
    if (legacy === null) return;
    clearLegacy();
    if (saved === null) void saveRef.current(legacy).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyTheme(pref);
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const setPref = (p: ThemePref) => {
    setPending(p);
    saveRef.current(p).finally(() => setPending(null));
  };
  return [pref, setPref] as const;
}
